/**
 * Staff roster sub-tab. Active staff + archived staff.
 * Add / edit / archive / unarchive via inline form modal.
 *
 * Web mirror of mobile `src/features/finance/tabs/StaffTab.tsx`.
 */
import { useMemo, useState } from 'react';

import { useAuth } from '@/auth/AuthProvider';
import {
  archiveStaff,
  createStaff,
  deleteStaff,
  unarchiveStaff,
  updateStaff,
  useStaff,
  type PayUnit,
  type Staff,
} from '@/lib/staff';

function fmtInr(n: number): string {
  return `₹${Math.round(n).toLocaleString('en-IN')}`;
}

type FormMode = { kind: 'closed' } | { kind: 'create' } | { kind: 'edit'; row: Staff };

export function StaffTab({ orgId }: { orgId: string | null }) {
  const { data, loading } = useStaff(orgId);
  const [formMode, setFormMode] = useState<FormMode>({ kind: 'closed' });
  const [showArchived, setShowArchived] = useState(false);

  const active = useMemo(() => data.filter((s) => !s.archivedAt), [data]);
  const archived = useMemo(() => data.filter((s) => !!s.archivedAt), [data]);

  const monthlyPayroll = useMemo(
    () => active.reduce((sum, s) => sum + (s.monthlySalary || 0), 0),
    [active],
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          gap: 12,
        }}
      >
        <Kpi label="Active staff"  value={String(active.length)}      tint="#3B82F6" />
        <Kpi label="Monthly payroll" value={fmtInr(monthlyPayroll)}   tint="#F59E0B" />
        <Kpi label="Archived"      value={String(archived.length)}    tint="#94A3B8" />
      </div>

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <button
          type="button"
          className="btn-secondary"
          onClick={() => setShowArchived((v) => !v)}
        >
          {showArchived ? 'Hide archived' : `Show archived (${archived.length})`}
        </button>
        <button
          type="button"
          className="btn-primary"
          onClick={() => setFormMode({ kind: 'create' })}
          disabled={!orgId}
        >
          <i className="ph ph-plus" /> Add staff
        </button>
      </div>

      {loading ? (
        <div style={{ padding: 24, color: 'var(--text-secondary)' }}>Loading…</div>
      ) : active.length === 0 && archived.length === 0 ? (
        <div
          style={{
            padding: 48,
            textAlign: 'center',
            color: 'var(--text-secondary)',
          }}
        >
          <i className="ph ph-users-three" style={{ fontSize: 48, opacity: 0.5 }} />
          <div style={{ marginTop: 12, fontWeight: 600 }}>No staff yet</div>
          <div style={{ marginTop: 4, fontSize: 14 }}>
            Add managers, supervisors, accountants, and other studio team.
          </div>
        </div>
      ) : (
        <>
          {active.length > 0 ? (
            <Section
              title={`Active (${active.length})`}
              rows={active}
              onEdit={(row) => setFormMode({ kind: 'edit', row })}
            />
          ) : null}
          {showArchived && archived.length > 0 ? (
            <Section
              title={`Archived (${archived.length})`}
              rows={archived}
              onEdit={(row) => setFormMode({ kind: 'edit', row })}
              dim
            />
          ) : null}
        </>
      )}

      {formMode.kind !== 'closed' ? (
        <StaffFormModal
          orgId={orgId}
          mode={formMode}
          onClose={() => setFormMode({ kind: 'closed' })}
        />
      ) : null}
    </div>
  );
}

function Kpi({ label, value, tint }: { label: string; value: string; tint: string }) {
  return (
    <div
      className="card"
      style={{
        padding: 12,
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderLeft: `4px solid ${tint}`,
        borderRadius: 10,
      }}
    >
      <div style={{ fontSize: 11, color: 'var(--text-secondary)', letterSpacing: 0.4 }}>
        {label}
      </div>
      <div style={{ marginTop: 4, fontSize: 20, fontWeight: 700 }}>{value}</div>
    </div>
  );
}

function Section({
  title,
  rows,
  onEdit,
  dim,
}: {
  title: string;
  rows: Staff[];
  onEdit: (row: Staff) => void;
  dim?: boolean;
}) {
  return (
    <div>
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: 'var(--text-tertiary)',
          textTransform: 'uppercase',
          letterSpacing: 0.4,
          marginBottom: 8,
        }}
      >
        {title}
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          opacity: dim ? 0.7 : 1,
        }}
      >
        {rows.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => onEdit(s)}
            style={{
              textAlign: 'left',
              padding: 12,
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 10,
              cursor: 'pointer',
              display: 'grid',
              gridTemplateColumns: '1fr auto',
              gap: 12,
              alignItems: 'center',
              font: 'inherit',
              color: 'inherit',
            }}
          >
            <div>
              <div style={{ fontWeight: 600, fontSize: 14 }}>{s.name}</div>
              <div style={{ marginTop: 2, fontSize: 12, color: 'var(--text-secondary)' }}>
                {s.role}
                {s.phone ? ` · ${s.phone}` : ''}
                {s.isOrgMember ? ' · App user' : ''}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>{fmtInr(s.monthlySalary)}</div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                {s.payUnit === 'month' ? '/ month' : '/ month (daily)'}
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ───── Form modal ───────────────────────────────────────

function StaffFormModal({
  orgId,
  mode,
  onClose,
}: {
  orgId: string | null;
  mode: { kind: 'create' } | { kind: 'edit'; row: Staff };
  onClose: () => void;
}) {
  const { user } = useAuth();
  const editing = mode.kind === 'edit' ? mode.row : null;
  const isArchived = !!editing?.archivedAt;

  const [name, setName] = useState(editing?.name ?? '');
  const [role, setRole] = useState(editing?.role ?? '');
  const [phone, setPhone] = useState(editing?.phone ?? '');
  const [salary, setSalary] = useState(
    editing ? String(editing.monthlySalary) : '',
  );
  const [payUnit, setPayUnit] = useState<PayUnit>(editing?.payUnit ?? 'month');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    if (!name.trim() || !role.trim()) {
      setError('Name and role are required.');
      return;
    }
    const ms = parseFloat(salary);
    if (!Number.isFinite(ms) || ms < 0) {
      setError('Monthly salary must be 0 or greater.');
      return;
    }
    if (!orgId || !user) {
      setError('Not ready — please reload.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      if (mode.kind === 'create') {
        await createStaff({
          orgId,
          name,
          phone: phone.trim() || undefined,
          role,
          monthlySalary: ms,
          payUnit,
          isOrgMember: false,
          createdBy: user.uid,
        });
      } else {
        await updateStaff(mode.row.id, {
          name,
          role,
          phone: phone.trim() || undefined,
          monthlySalary: ms,
          payUnit,
        });
      }
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save.');
    } finally {
      setBusy(false);
    }
  }

  async function toggleArchive() {
    if (!editing) return;
    setBusy(true);
    try {
      if (isArchived) await unarchiveStaff(editing.id);
      else await archiveStaff(editing.id);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not update.');
      setBusy(false);
    }
  }

  async function remove() {
    if (!editing) return;
    if (
      !confirm(
        `Hard-delete ${editing.name}? Their attendance + payroll history will remain but the staff doc is removed. Consider Archive instead.`,
      )
    )
      return;
    setBusy(true);
    try {
      await deleteStaff(editing.id);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not delete.');
      setBusy(false);
    }
  }

  return (
    <>
      <div
        className="slide-overlay open"
        onClick={() => !busy && onClose()}
        style={{ background: 'rgba(15,23,42,0.45)' }}
      />
      <div className="slide-over open" style={{ width: 460 }}>
        <div
          style={{
            padding: '14px 18px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <h2 style={{ margin: 0, fontSize: 18 }}>
            {mode.kind === 'create' ? 'Add staff' : editing?.name}
          </h2>
          <button
            type="button"
            onClick={() => !busy && onClose()}
            style={{
              background: 'transparent',
              border: 'none',
              fontSize: 20,
              cursor: 'pointer',
              color: 'var(--text-secondary)',
            }}
            aria-label="Close"
          >
            <i className="ph ph-x" />
          </button>
        </div>

        <div
          style={{
            padding: 18,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            flex: 1,
          }}
        >
          <Field label="Name *">
            <input
              className="input"
              type="text"
              value={name}
              onChange={(e) => {
                const v = e.currentTarget.value;
                setName(v);
              }}
              autoFocus
            />
          </Field>
          <Field label="Role / title *">
            <input
              className="input"
              type="text"
              value={role}
              onChange={(e) => {
                const v = e.currentTarget.value;
                setRole(v);
              }}
              placeholder="Site Engineer, Accountant…"
            />
          </Field>
          <Field label="Phone">
            <input
              className="input"
              type="tel"
              value={phone}
              onChange={(e) => {
                const v = e.currentTarget.value;
                setPhone(v);
              }}
            />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Field label="Monthly salary (₹) *">
              <input
                className="input"
                type="number"
                value={salary}
                onChange={(e) => {
                  const v = e.currentTarget.value;
                  setSalary(v);
                }}
              />
            </Field>
            <Field label="Pay unit">
              <select
                className="input"
                value={payUnit}
                onChange={(e) => {
                  const v = e.currentTarget.value as PayUnit;
                  setPayUnit(v);
                }}
              >
                <option value="month">Monthly</option>
                <option value="day">Daily (derived)</option>
              </select>
            </Field>
          </div>
          {error ? (
            <div
              style={{
                padding: 10,
                borderRadius: 8,
                background: '#FEE2E2',
                color: '#B91C1C',
                fontSize: 13,
              }}
            >
              {error}
            </div>
          ) : null}
        </div>

        <div
          style={{
            padding: '12px 18px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            gap: 8,
          }}
        >
          {mode.kind === 'edit' ? (
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                type="button"
                onClick={toggleArchive}
                disabled={busy}
                className="btn-secondary"
                style={{ color: 'var(--text-secondary)' }}
              >
                {isArchived ? 'Unarchive' : 'Archive'}
              </button>
              <button
                type="button"
                onClick={remove}
                disabled={busy}
                style={{
                  background: 'transparent',
                  border: '1px solid #fecaca',
                  color: '#b91c1c',
                  padding: '6px 12px',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: 13,
                }}
              >
                Delete
              </button>
            </div>
          ) : (
            <span />
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={save}
              disabled={busy}
            >
              {busy ? 'Saving…' : mode.kind === 'create' ? 'Add' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500 }}>
        {label}
      </span>
      {children}
    </label>
  );
}
